// Ask Allen demo config — the scripted chips + canned answers used by the docked
// companion on the demo store tree (where there's no live backend). Extracted so
// the global AllenDock can reuse it (was inline in StoralyticDemoHomePage).
//
// Dutch alongside (demo/demoLang.js): the chips, and per answer its Dutch text plus the
// Dutch words a question is matched on — read in the portal's current language. Same
// figures in both.
import { withNl, listWithNl } from '@/demo/demoLang'

export const DEMO_ALLEN_CHIPS = listWithNl([
  'What should I focus on first?',
  'How is Power Tools doing?',
  'When is the store busiest?',
], [
  'Waar moet ik eerst op focussen?',
  'Hoe doet Power Tools het?',
  'Wanneer is het het drukst in de winkel?',
])

export const DEMO_ALLEN_ANSWERS = [
  withNl({ keywords: ['focus', 'opportunit', 'priorit', 'first', 'where should', 'biggest'],
    answer: 'Your biggest opportunity is Garden Utilities — about €21,000 within reach, and engagement is the ' +
      'lever. Lots of shoppers pass it (around 42% of footfall) but only about 22% stop; a focal display or ' +
      'clearer signage would get more of them to pause.' }, {
    keywords: ['focus', 'kans', 'prioriteit', 'eerst', 'waar moet', 'grootste'],
    answer: 'Je grootste kans is Garden Utilities: zo\'n € 21.000 te winnen, en engagement is de ' +
      'hefboom. Veel bezoekers komen erlangs (ongeveer 42% van je winkelbezoekers), maar slechts zo\'n 22% blijft staan. ' +
      'Een opvallende display of duidelijkere bewegwijzering laat meer van hen even stilstaan.',
  }),
  withNl({ keywords: ['garden'],
    answer: 'Garden Utilities pulls strong traffic — about 42% of your shoppers pass it — but only ~22% stop to ' +
      'engage. Engagement is the lever (roughly €21,000 within reach): a focal display or clearer signage would ' +
      'help more people pause.' }, {
    keywords: ['garden', 'tuin'],
    answer: 'Garden Utilities trekt veel bezoekers: zo\'n 42% van je bezoekers komt erlangs, maar slechts ~22% ' +
      'blijft staan. Engagement is de hefboom (ongeveer € 21.000 te winnen): een opvallende display of duidelijkere ' +
      'bewegwijzering helpt meer mensen even stil te staan.',
  }),
  withNl({ keywords: ['busiest', 'busy', 'when', 'peak', 'hour', 'time of day'],
    answer: 'Your store is busiest mid-morning — the 10–11am hour is the peak, around 55 visitors on average — ' +
      'then it eases off through the afternoon.' }, {
    keywords: ['drukst', 'druk', 'wanneer', 'welk uur', 'tijdstip'],
    answer: 'Je winkel is het drukst halverwege de ochtend: het drukste uur is tussen 10:00 en 11:00, met gemiddeld ' +
      'zo\'n 55 bezoekers. Daarna wordt het in de loop van de middag rustiger.',
  }),
  withNl({ keywords: ['promoted', 'articles'],
    answer: 'The Promoted Articles Zone gets modest traffic, and the shoppers who do stop mostly don\'t buy — ' +
      'conversion is only about 14%. Turning more of those stops into purchases is the lever there.' }, {
    keywords: ['promoted', 'articles', 'promo'],
    answer: 'De Promoted Articles Zone krijgt een bescheiden aantal bezoekers, en wie er blijft staan, koopt meestal niet: ' +
      'de conversie is maar zo\'n 14%. Meer van die bezoekers die blijven staan laten kopen, is daar de hefboom.',
  }),
  withNl({ keywords: ['safety', 'shoes'],
    answer: 'Safety Shoes engages and converts well, but few shoppers reach it — only about 7% of footfall, ' +
      'versus ~15% at its best. Reach is the lever (roughly €16,700 within reach): clearer signage or a nearer ' +
      'endcap would bring more people to it.' }, {
    keywords: ['safety', 'shoes', 'schoen'],
    answer: 'Safety Shoes scoort goed op engagement en conversie, maar weinig bezoekers komen er: slechts zo\'n 7% van je ' +
      'winkelbezoekers, tegenover ~15% in de beste weken. Bereik is de hefboom (ongeveer € 16.700 te winnen): duidelijkere ' +
      'bewegwijzering of een kopstelling dichter bij de looproute brengt er meer mensen naartoe.',
  }),
  withNl({ keywords: ['power tools', 'power', 'tools'],
    answer: 'Power Tools is performing well — solid engagement (~50%) and conversion (~46%), right at its ' +
      'benchmark. Keep it up; your bigger € opportunities are Garden Utilities (engagement) and Safety Shoes (reach).' }, {
    keywords: ['power tools', 'power', 'tools', 'gereedschap'],
    answer: 'Power Tools presteert goed: stevige engagement (~50%) en conversie (~46%), precies op het niveau van de ' +
      'benchmark. Ga zo door; je grotere kansen in € liggen bij Garden Utilities (engagement) en Safety Shoes (bereik).',
  }),
  withNl({ keywords: ['checkout', 'queue', 'wait', 'service', 'staff'],
    answer: 'Your checkout is keeping up — service efficiency is around 67%, with short waits at the peak. ' +
      'Aligning staffing to the mid-morning rush would tighten it further.' }, {
    keywords: ['kassa', 'wachtrij', 'wacht', 'service', 'medewerker', 'bezetting'],
    answer: 'Je kassa houdt het bij: de service aan de kassa scoort zo\'n 67%, met korte wachttijden op het drukste moment. ' +
      'Als je de kassabezetting afstemt op de drukte halverwege de ochtend, wordt dat nog beter.',
  }),
  withNl({ keywords: ['who are you', 'what are you', 'what do you do', 'your purpose'],
    answer: "I'm Allen, Storalytic's in-store companion. I read your store's camera-event data each day and " +
      "turn it into plain-English answers about what's happening and where to focus. Try one of the questions below!" }, {
    keywords: ['wie ben je', 'wat ben je', 'wat doe je', 'wat is je doel'],
    answer: 'Ik ben Allen, de winkelcoach van Storalytic. Ik lees elke dag de cameragegevens van je winkel en ' +
      'zet die om in heldere antwoorden over wat er gebeurt en waar je je op moet richten. Probeer hieronder een van de vragen!',
  }),
  withNl({ keywords: ['how are you'],
    answer: 'Doing great, thanks — and ready to dig into store data! In this demo, try one of the suggested ' +
      'questions below.' }, {
    keywords: ['hoe gaat het', 'alles goed'],
    answer: 'Prima, dank je — en klaar om in de winkeldata te duiken! Probeer in deze demo een van de voorgestelde ' +
      'vragen hieronder.',
  }),
]
